// @systeme: mesure-impact
// ============================================================================
// PairsForm - Page Protocole de Mesure d'Impact
// ============================================================================
// Page publique décrivant le protocole de mesure d'impact pour les participants
// Accessible via lien dans les emails de questionnaires
// ============================================================================

import { CheckCircle, ChevronDown, Lock, Shield, TrendingUp, Users } from "lucide-react";
import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Divider,
  Chip,
} from "@mui/material";

export default function ProtocoleMesureImpactPage() {
  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", py: 6 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Paper elevation={0} sx={{ p: 4, mb: 4, bgcolor: "white", borderRadius: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "12px",
                bgcolor: "#4BB7A5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TrendingUp size={32} color="white" />
            </Box>
            <Box>
              <Typography variant="h4" component="h1" sx={{ color: "#334155", fontWeight: 600 }}>
                Protocole de Mesure d'Impact
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PairsForm - Mesure scientifique des impacts de formation
              </Typography>
            </Box>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Participation volontaire</strong> - Ce questionnaire est anonyme et votre
              participation est totalement volontaire. En soumettant vos réponses, vous acceptez le
              traitement de vos données conformément aux législations applicables et aux dispositions
              de ce protocole.
            </Typography>
          </Alert>
        </Paper>

        {/* Pourquoi mesurer */}
        <Paper elevation={0} sx={{ p: 4, mb: 3, bgcolor: "white", borderRadius: 3 }}>
          <Typography variant="h5" sx={{ mb: 2, color: "#334155", fontWeight: 600 }}>
            📊 Pourquoi mesurer les impacts ?
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Les parcours de formation et de développement professionnel génèrent des impacts concrets
            sur vos compétences, votre posture et votre efficacité au travail. Ce dispositif de
            mesure permet d'<strong>objectiver ces évolutions</strong> et de :
          </Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 1 } }}>
            <li>
              <Typography variant="body1">
                <strong>Prendre conscience</strong> de vos progrès réels
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Valoriser</strong> les acquis de votre parcours
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Contribuer</strong> à l'amélioration continue des formations
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Bénéficier</strong> d'un retour structuré sur votre développement
              </Typography>
            </li>
          </Box>
        </Paper>

        {/* Comment ça fonctionne */}
        <Paper elevation={0} sx={{ p: 4, mb: 3, bgcolor: "white", borderRadius: 3 }}>
          <Typography variant="h5" sx={{ mb: 2, color: "#334155", fontWeight: 600 }}>
            🔄 Comment ça fonctionne ?
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Le protocole repose sur une mesure <strong>avant et après</strong> votre parcours pour
            objectiver vos évolutions.
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 3 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Chip
                label="T0"
                sx={{ bgcolor: "#E1512C", color: "white", minWidth: 50, fontWeight: 600 }}
              />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Avant le parcours
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Établit votre point de départ (15-30 minutes)
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Chip
                label="T1"
                sx={{ bgcolor: "#F97316", color: "white", minWidth: 50, fontWeight: 600 }}
              />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Mi-parcours (optionnel)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Permet d'identifier les évolutions précoces (10 minutes)
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Chip
                label="T2"
                sx={{ bgcolor: "#4BB7A5", color: "white", minWidth: 50, fontWeight: 600 }}
              />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Fin de parcours
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Mesure vos progrès à chaud (20-30 minutes)
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Chip
                label="T3"
                sx={{ bgcolor: "#059669", color: "white", minWidth: 50, fontWeight: 600 }}
              />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Suivi différé (optionnel)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  3-6 mois après : mesure la durabilité des acquis (15 minutes)
                </Typography>
              </Box>
            </Box>
          </Box>

          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body2">
              ✅ <strong>Questionnaires validés scientifiquement</strong> - Tous les outils utilisés
              sont des questionnaires validés par la recherche internationale, traduits et adaptés
              en français.
            </Typography>
          </Alert>
        </Paper>

        {/* Confidentialité et protection des données */}
        <Paper elevation={0} sx={{ p: 4, mb: 3, bgcolor: "white", borderRadius: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Lock size={32} color="#4BB7A5" />
            <Typography variant="h5" sx={{ color: "#334155", fontWeight: 600 }}>
              Confidentialité et protection des données
            </Typography>
          </Box>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Anonymat garanti</strong> - Vos réponses sont strictement confidentielles et
              traitées de manière anonyme dans un groupe de minimum 6 participants.
            </Typography>
          </Alert>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ChevronDown size={20} />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Shield size={18} color="#4BB7A5" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  🔒 Garanties de confidentialité
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box component="ul" sx={{ pl: 2, "& li": { mb: 1.5 } }}>
                <li>
                  <Typography variant="body2">
                    <strong>Anonymisation totale</strong> : Vos données sont associées à un code
                    anonyme dès la collecte
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Pas d'accès nominatif</strong> : Votre hiérarchie ou RH ne verra JAMAIS
                    vos réponses individuelles
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Agrégation des résultats</strong> : Seules des statistiques de groupe
                    (minimum 6 participants) sont communiquées à l'organisation
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Sécurisation renforcée</strong> : Données chiffrées et accès strictement
                    limités
                  </Typography>
                </li>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ChevronDown size={20} />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircle size={18} color="#4BB7A5" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  ⚖️ Conformité RGPD
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                Les questionnaires utilisés sont considérés comme des <strong>données sensibles</strong> selon
                l'article 9 du RGPD. Votre organisation s'engage à :
              </Typography>
              <Box component="ul" sx={{ pl: 2, "& li": { mb: 1 } }}>
                <li>
                  <Typography variant="body2">
                    Recueillir votre consentement explicite et éclairé
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">Sécuriser vos données par chiffrement</Typography>
                </li>
                <li>
                  <Typography variant="body2">Limiter strictement les accès</Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Respecter des durées de conservation définies (3 ans maximum)
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Vous garantir vos droits (accès, rectification, effacement, limitation,
                    opposition, portabilité)
                  </Typography>
                </li>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ChevronDown size={20} />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Users size={18} color="#4BB7A5" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  ✅ Vos droits
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                Votre participation est <strong>totalement volontaire</strong>. Vous pouvez :
              </Typography>
              <Box component="ul" sx={{ pl: 2, "& li": { mb: 1 } }}>
                <li>
                  <Typography variant="body2">
                    Refuser de participer sans avoir à vous justifier
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Retirer votre consentement à tout moment
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">Demander l'effacement de vos données</Typography>
                </li>
                <li>
                  <Typography variant="body2">Accéder à vos données personnelles</Typography>
                </li>
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Important :</strong> Votre refus de participer n'aura aucune conséquence
                  sur votre participation au parcours ni sur votre évaluation professionnelle.
                </Typography>
              </Alert>
            </AccordionDetails>
          </Accordion>
        </Paper>

        {/* Consentement */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 3,
            bgcolor: "#fff7ed",
            border: "2px solid #F97316",
            borderRadius: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, color: "#334155", fontWeight: 600 }}>
            📝 Consentement éclairé
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>En soumettant vos réponses à ce questionnaire</strong>, vous déclarez :
          </Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 1 } }}>
            <li>
              <Typography variant="body1">
                Avoir pris connaissance de ce protocole de mesure d'impact
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Comprendre que vos données seront traitées de manière anonyme dans un groupe d'au
                moins 6 participants
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Consentir au traitement de vos données conformément aux législations applicables
                (RGPD) et aux dispositions de ce protocole
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Être informé(e) de vos droits (accès, rectification, effacement, opposition)
              </Typography>
            </li>
          </Box>
        </Paper>

        {/* FAQ */}
        <Paper elevation={0} sx={{ p: 4, mb: 3, bgcolor: "white", borderRadius: 3 }}>
          <Typography variant="h5" sx={{ mb: 3, color: "#334155", fontWeight: 600 }}>
            ❓ Questions fréquentes
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Puis-je refuser de participer ?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Oui, absolument. Votre participation est totalement volontaire et n'a aucun impact
                sur votre participation au parcours de formation.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Ma hiérarchie aura-t-elle accès à mes réponses individuelles ?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Non, jamais. Seules des statistiques agrégées anonymes (groupe de minimum 6
                participants) sont communiquées à l'organisation.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Combien de temps vais-je passer sur les questionnaires ?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Entre 15 et 35 minutes par passation selon le protocole. La plupart des protocoles
                nécessitent 2 passations (avant/après), soit 30-60 minutes au total.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Les questionnaires sont-ils difficiles ?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Non. Ce sont des questions simples à choix multiples. Il n'y a pas de bonnes ou de
                mauvaises réponses.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Combien de temps mes données sont-elles conservées ?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Les données anonymisées sont conservées 3 ans maximum pour permettre l'analyse des
                évolutions.
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Contact */}
        <Paper elevation={0} sx={{ p: 4, bgcolor: "white", borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: "#334155", fontWeight: 600 }}>
            📧 Contact et informations
          </Typography>
          <Typography variant="body2" paragraph>
            Pour toute question sur ce protocole de mesure, vos droits ou la protection de vos
            données, contactez le responsable du dispositif dans votre organisation.
          </Typography>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>En cas de difficulté :</strong> Vous avez le droit d'introduire une
              réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des
              Libertés) : www.cnil.fr
            </Typography>
          </Alert>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
            Ce protocole a été élaboré sur la base de questionnaires validés scientifiquement et
            dans le respect strict du Règlement Général sur la Protection des Données (RGPD).
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

